// front/src/App.js

/* eslint-disable react-hooks/exhaustive-deps */
import "./i18n/_keys";

import React, { useEffect, lazy, Suspense } from "react";
import "./App.css";
import "swiper/css";
import "swiper/css/navigation";

import { Routes, Route, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import { ErrorBoundary } from "./utils/ErrorBoundray";
import { ExpertProvider } from "./context";
import useMobileIndicatorStore from "./app/store/useMobileIndicatorStore";

// ****** i18n ****** //
import { useI18n } from "./i18n/I18nProvider";

// *********************************** Components *********************************** //
const PurcheseHistory = lazy(() => import("./components/purcheseHistory/PurcheseHistory"));
const TicketReply = lazy(() => import("./components/ticket/details/TicketDetails"));
const PaymentResultLayout = lazy(() => import("./layouts/PaymentResultLayout"));
const PaymentResult = lazy(() => import("./components/payments/PaymentResult"));
const SettingsLayout = lazy(() => import("./layouts/SettingsLayout"));
const PrivateRoutes = lazy(() =>
  import("./components").then((module) => ({ default: module.PrivateRoutes }))
);
const SignTemplate = lazy(() =>
  import("./components").then((module) => ({ default: module.SignTemplate }))
);

// *********************************** Main Pages *********************************** //
const Login = lazy(() => import("./pages").then((module) => ({ default: module.Login })));
const Register = lazy(() => import("./pages").then((module) => ({ default: module.Register })));
const NotFound = lazy(() => import("./pages").then((module) => ({ default: module.NotFound })));
const Ticket = lazy(() => import("./pages").then((module) => ({ default: module.Ticket })));
const NewTicket = lazy(() => import("./pages").then((module) => ({ default: module.NewTicket })));
const Advanced = lazy(() => import("./pages").then((module) => ({ default: module.Advanced })));
const Alerts = lazy(() => import("./pages").then((module) => ({ default: module.Alerts })));
const Beginner = lazy(() => import("./pages").then((module) => ({ default: module.Beginner })));
const Expert = lazy(() => import("./pages").then((module) => ({ default: module.Expert })));
const NotificationPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.NotificationPage }))
);
const CreateNotificationForm = lazy(() =>
  import("./pages").then((module) => ({
    default: module.CreateNotificationForm,
  }))
);
const WatchlistPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.WatchlistPage }))
);
const WatchlistDetails = lazy(() =>
  import("./pages").then((module) => ({ default: module.WatchlistDetails }))
);
const AlertsSinglePage = lazy(() =>
  import("./pages").then((module) => ({ default: module.AlertsSinglePage }))
);
const Profile = lazy(() => import("./pages").then((module) => ({ default: module.Profile })));
const BacktestsPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.BacktestsPage }))
);
const BacktestDetailPage = lazy(() =>
  import("./pages").then((module) => ({ default: module.BacktestDetailPage }))
);
const StandaloneChartPage = lazy(() => import("./pages/chart/StandaloneChartPage"));

const PlanPage = lazy(() => import("./pages/plans/PlanPage"));

// ✅ NEW: Telegram connect page (nested under /setting)
const TelegramConnect = lazy(() => import("./pages/setting/TelegramConnect"));

// ✅ NEW: Professional legal pages (public)
const TermsPage = lazy(() => import("./pages/legal/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/legal/PrivacyPage"));
const ChartFocusLab = lazy(() => import("./pages/chartFocusLab/ChartFocusLab"));

function App() {
  const { setMobileIndicatorKey } = useMobileIndicatorStore();
  const location = useLocation();

  // ✅ i18n direction
  const { isRtl } = useI18n();

  useEffect(() => {
    setMobileIndicatorKey(null);
  }, [location]);

  return (
    <div className="App">
      <Suspense fallback={<div></div>}>
        <ToastContainer
          position={isRtl ? "top-left" : "top-right"} // ✅ better UX for RTL
          autoClose={3000}
          hideProgressBar
          newestOnTop={false}
          closeOnClick
          rtl={isRtl} // ✅ dynamic RTL/LTR
          pauseOnFocusLoss={false}
          draggable={false}
          pauseOnHover={false}
          theme="colored"
          limit={4}
          toastClassName="ajna-toast" // ✅ hook for font/dir css
          bodyClassName="ajna-toast__body"
        />

        <ErrorBoundary>
          <Routes>
            {/* ✅ Public legal pages */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />

            {/* Local-only TradingView viewport laboratory. */}
            <Route
              path="/chart-focus-lab"
              element={process.env.NODE_ENV === "production" ? <NotFound /> : <ChartFocusLab />}
            />

            {/* Unified tabbed auth page */}
            <Route element={<SignTemplate />}>
              <Route path="/signin" element={<Login />} />
              <Route path="/support/login" element={<Login mode="support" />} />
              {/* Keep /signup route but use the same unified page */}
              <Route path="/signup" element={<Register />} />
            </Route>

            {/* ✅ Payment Result routes MUST be outside PrivateRoutes (fullscreen, no sidebar/layout) */}
            <Route element={<PaymentResultLayout />}>
              <Route path="/success" element={<PaymentResult />} />
              <Route path="/faield" element={<PaymentResult />} />
            </Route>

            {/* Main app (protected routes) */}
            <Route element={<PrivateRoutes />}>
              <Route path="" element={<Beginner />} />
              <Route path="advanced" element={<Advanced />} />
              <Route path="chart" element={<StandaloneChartPage />} />
              <Route path="watchlist" element={<WatchlistPage />} />
              <Route path="watchlist/:id" element={<WatchlistDetails />} />
              <Route path="backtests" element={<BacktestsPage />} />
              <Route path="backtests/:id" element={<BacktestDetailPage />} />
              <Route
                path="expert"
                element={
                  <ExpertProvider>
                    <Expert />
                  </ExpertProvider>
                }
              />

              <Route path="notification" element={<NotificationPage />} />
              <Route path="notification/create" element={<CreateNotificationForm />} />

              <Route path="alerts" element={<Alerts />} />
              <Route path="alerts/:id" element={<AlertsSinglePage />} />
              <Route path="ticket" element={<Ticket />} />
              <Route path="ticket/:id" element={<TicketReply />} />
              <Route path="new_ticket" element={<NewTicket />} />

              {/* Support panel routes */}
              <Route path="support/tickets" element={<Ticket />} />
              <Route path="support/tickets/:id" element={<TicketReply />} />
              <Route path="support/new-ticket" element={<NewTicket />} />

              <Route path="/setting" element={<SettingsLayout />}>
                <Route index element={<Profile />} />
                <Route path="purchase" element={<PurcheseHistory />} />
                <Route path="plans" element={<PlanPage />} />

                {/* ✅ NEW: /setting/telegram */}
                <Route path="telegram" element={<TelegramConnect />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </Suspense>
    </div>
  );
}

export default App;
